import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Link,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Stack
} from '@mui/material';
import PinterestIcon from '@mui/icons-material/Pinterest';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkIcon from '@mui/icons-material/Link';
import { useEffect } from 'react';
import { STATUS } from '../settings/constants';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { StatusBlock } from '../wordpress/history';
import {
  ArrowBackIos,
  ArrowForwardIos,
  ArrowRight,
  FilterNone,
  Replay,
  UndoRounded
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const GenerationSkippedCard = ({ post }) => {
  const navigate = useNavigate();
  const visitSite = () => {
    navigate('/wordpress_sites/' + post.siteId);
  };
  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.3s',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: 6
        }
      }}
    >
      <Stack height="300px" gap={2} justifyContent={'center'} alignContent="center" padding={2}>
        <Typography textAlign={'center'} variant="h6">
          Generation was skipped as no posts were found to recycle or we ran out of new posts.
        </Typography>

        <Button endIcon={<ArrowRight />} onClick={visitSite}>
          Visit site settings
        </Button>
      </Stack>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {new Date(post.timestamp).toLocaleDateString()}
          </Typography>

          <StatusBlock status={'SKIPPED'} />
        </Box>
      </CardContent>
    </Card>
  );
};
const PostCard = ({ initialData }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [post, setPost] = useState(initialData);
  const [imageIndex, setImageIndex] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasRetried, setHasRetried] = useState(false);

  useEffect(() => {
    if (!hasRetried && ![STATUS.PROCESSING, STATUS.QUEUED].includes(initialData.status)) return;

    const unsubscribe = onSnapshot(
      doc(getFirestore(), 'social_posts', initialData.doc_id),
      (doc) => {
        const data = doc.data();
        if (data) {
          setPost((prevPost) => ({
            ...prevPost,
            ...data
          }));
        }
      }
    );
    return () => {
      unsubscribe();
    };
  }, [initialData, hasRetried]);

  // Determine if the post is from Pinterest or Instagram
  const isPinterest = post.type === 'pinterest_pins';
  const isInstagram = post.type === 'instagram_posts';

  // Get the appropriate social media link
  const getSocialMediaLink = () => {
    if (isPinterest) {
      return `https://pinterest.com/pin/${post.pinId}`;
    } else if (isInstagram) {
      return `https://instagram.com/p/${post.postId}`;
    }
    return '#';
  };

  const submitForRetry = async () => {
    if (isRetrying || hasRetried) return;

    setIsRetrying(true);
    try {
      const fn = httpsCallable(getFunctions(), 'pinterestRequest');
      await fn({
        historyId: post.doc_id,
        siteId: post.siteId,
        command: 'relaunchPinterestOrInstagram'
      });
      setHasRetried(true);
      console.log('Task submitted successfully!');
    } catch (error) {
      console.error('Error retrying post:', error);
      toast.error('Failed to retry post. Please try again later.');
    } finally {
      setIsRetrying(false);
    }
  };

  // Truncate description to a reasonable length
  const truncateDescription = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (post?.status === STATUS.ERROR && post?.errors?.[0] === 'No new posts to process') {
    return <GenerationSkippedCard post={post} />;
  }

  //

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.3s',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: 6
        }
      }}
    >
      <Box
        sx={{ position: 'relative' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {post?.imageUrls?.length > 1 && (
          <>
            <FilterNone
              style={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 1,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: '5px',
                borderRadius: '4px'
              }}
            />

            {isHovered && (
              <>
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 100,
                    right: 10,
                    zIndex: 1,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)'
                    }
                  }}
                  onClick={() => {
                    setImageIndex((prevIndex) => (prevIndex + 1) % post.imageUrls.length);
                  }}
                >
                  <ArrowForwardIos />
                </IconButton>

                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 100,
                    left: 10,
                    zIndex: 1,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)'
                    }
                  }}
                  onClick={() => {
                    setImageIndex(
                      (prevIndex) => (prevIndex - 1 + post.imageUrls.length) % post.imageUrls.length
                    );
                  }}
                >
                  <ArrowBackIos />
                </IconButton>
              </>
            )}
          </>
        )}
        <CardMedia
          component="img"
          height="240"
          image={post?.imageUrls?.[imageIndex] || '/assets/images/default-fallback-image.png'}
          sx={{ objectFit: 'cover' }}
        />

        {/* Platform type indicator */}
        <Chip
          icon={isPinterest ? <PinterestIcon /> : <InstagramIcon />}
          label={isPinterest ? 'Pinterest' : 'Instagram'}
          size="small"
          color={isPinterest ? 'error' : 'primary'}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1
          }}
        />

        {isHovered && post?.WPpostId && !hasRetried && (
          <Chip
            size="small"
            label={isRetrying ? 'retrying .. ' : 'Retry'}
            color={isPinterest ? 'error' : 'primary'}
            sx={{
              position: 'absolute',

              top: 10,
              left: 10,
              zIndex: 1
            }}
            onClick={submitForRetry}
            icon={
              isRetrying ? <CircularProgress size={14} style={{ color: 'white' }} /> : <Replay />
            }
          ></Chip>
        )}

        {/* WordPress link overlay - only visible on hover */}
        {isHovered && post.WPLink && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,

              color: 'white',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.3s'
            }}
          >
            <Link
              href={post.WPLink}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <LinkIcon sx={{ mr: 0.5 }} />
              View WordPress Post
            </Link>
          </Box>
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, height: '60px', overflow: 'auto' }}
        >
          {post.status === STATUS.DONE
            ? truncateDescription(post.description)
            : post.status === STATUS.PROCESSING
            ? post.message
            : post?.errors?.map((error, index) => (
                <Typography key={index} variant="body2" color="error">
                  {error}
                </Typography>
              ))}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {new Date(post.timestamp).toLocaleDateString()}
          </Typography>

          {post.status !== STATUS.DONE ? (
            <StatusBlock status={post.status} />
          ) : (
            <Button
              size="small"
              component={Link}
              href={getSocialMediaLink()}
              target="_blank"
              rel="noopener noreferrer"
              color={isPinterest ? 'error' : 'primary'}
              startIcon={isPinterest ? <PinterestIcon /> : <InstagramIcon />}
            >
              view on {isPinterest ? 'Pinterest' : 'Instagram'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PostCard;
